import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconArrowRight, IconArrowLeft } from '@tabler/icons-react';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import TemplatesSection from '../components/email-templates/templates-section';
import { templateTypes, WELCOME_SERIES } from '../core/constants';
import appSettings from '../app-settings';
import Loader from '../components/loader/Loader';
import { useEmailTemplates } from '../store/email-templates/hooks';
import Button from '../components/buttons/Button';
import IconButton from '../components/buttons/IconButton';
import { LaunchWelcomeSeriesButton, LaunchSecondWelcomeSeriesButton } from '../components/welcome-series';

const basicTemplates = [
  {
    title: 'Blank',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.single,
  },
  {
    title: 'Letter',
    image: 'letter.svg',
    source: 'letter.json',
    type: templateTypes.single,
  },
  {
    title: 'Announcement',
    image: 'announcement.svg',
    source: 'announcement.json',
    type: templateTypes.single,
  },
  {
    title: 'Multi-Button Contribution',
    image: 'multi-button-contribution.svg',
    source: 'multi-button-contribution.json',
    type: templateTypes.single,
  },
];

const firstSeriesRecurringTemplates = [
  {
    title: 'Welcome Email #1',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.recurring,
    order: 0,
  },
  {
    title: 'Welcome Email #2',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.recurring,
    order: 1,
  },
  {
    title: 'Welcome Email #3',
    image: 'blank.svg',
    source: 'blank.json',
    type: templateTypes.recurring,
    order: 2,
  },
  {
    title: 'Welcome Email #4',
    image: 'letter.svg',
    source: 'welcome-4.json',
    type: templateTypes.recurring,
    order: 3,
  },
];

// Second welcome series: 5 emails at template order 4-8.
const secondSeriesRecurringTemplates = Array.from({ length: WELCOME_SERIES.second.count }, (_, index) => ({
  title: `Second Welcome Email #${index + 1}`,
  image: 'blank.svg',
  source: 'blank.json',
  type: templateTypes.recurring,
  order: WELCOME_SERIES.second.startOrder + index,
}));

const getInitialTemplates = (isSecondSeries) => ({
  basicTemplates,
  recurringTemplates: (isSecondSeries ? secondSeriesRecurringTemplates : firstSeriesRecurringTemplates).map(
    (template) => ({ ...template }),
  ),
});
const EMAIL_TEMPLATES_URL = `${appSettings.baseUrl}/email-templates`;

const EmailTemplates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSecondSeries = location.state?.series === 2;
  const [templates, setTemplates] = useState(() => getInitialTemplates(isSecondSeries));
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const { resetTemplate, updateTemplate } = useEmailTemplates();

  const getTemplates = useCallback(async () => {
    if (location.state?.type !== templateTypes.recurring) {
      setLoading(false);
      return;
    }
    try {
      const { data: savedTemplates } = await axios.get(EMAIL_TEMPLATES_URL, {
        params: { type: templateTypes.recurring },
      });

      // Merge saved templates into the matching slot by order (slots are 0-3 for the
      // first series or 4-8 for the second series). Saved templates outside the
      // current series' order range are ignored.
      savedTemplates.forEach(({ design, downloadUrl, order, templateId, templateVersion, emailConfig, type }) => {
        const slotIndex = templates.recurringTemplates.findIndex((t) => t.order === order);
        if (slotIndex === -1) return;
        templates.recurringTemplates[slotIndex] = {
          ...templates.recurringTemplates[slotIndex],
          design,
          downloadUrl,
          templateId,
          templateVersion,
          emailConfig,
          order,
          type,
        };
      });

      setTemplates(templates);
    } catch (err) {
      Sentry.captureException(err);
    }
    setLoading(false);
  }, [location.state?.type, templates]);

  useEffect(() => {
    getTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRecurring = location.state?.type === templateTypes.recurring;

  const setSelectedTemplateAndContinue = () => {
    resetTemplate();
    updateTemplate(selectedTemplate);
    navigate(isRecurring ? '/email-config' : '/email-editor', {
      state: { selectedTemplate },
    });
  };

  const handleGoBack = () => {
    navigate(isRecurring ? '/welcome-series' : '/new-campaign', {
      replace: true,
    });
  };

  return (
    <div className="relative w-full space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1">
          <IconButton
            Icon={<IconArrowLeft size={20} />}
            color="transparent"
            className="-ml-2"
            onClick={handleGoBack}
            srOnly="Go back"
          />

          <h1 className="text-h3">
            {isRecurring ? (isSecondSeries ? 'Select a Second Welcome Email' : 'Select an Email') : 'Select a Template'}
          </h1>
        </div>

        {isRecurring && (isSecondSeries ? <LaunchSecondWelcomeSeriesButton /> : <LaunchWelcomeSeriesButton />)}
      </div>

      <div className="relative min-h-[50px] w-full">
        {!loading && (
          <TemplatesSection
            key="Basic"
            title="Basic"
            templates={isRecurring ? templates.recurringTemplates : templates.basicTemplates}
            selectTemplate={(t) => setSelectedTemplate(t)}
            selectedTemplate={selectedTemplate}
          />
        )}

        {loading && <Loader />}
      </div>

      <div className="flex justify-end">
        <Button
          title="Continue"
          onClick={setSelectedTemplateAndContinue}
          disabled={!selectedTemplate}
          RightIcon={IconArrowRight}
        />
      </div>
    </div>
  );
};

export default EmailTemplates;
